import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import api from "../../api";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../../utils/toastMessages";
import crossIcon from "../../assets/icons/crossIcon.png";
import fileIcon from "../../assets/icons/sboxfile.png";
import linkIcon from "../../assets/icons/sboxlink.png";
import { BsArrowRepeat } from "react-icons/bs";
import { toast } from "react-toastify";
import { formatDateToUserTimeZone } from "../../utils/formatDate";
import axios from "axios";
import { useTableSort } from "../../hooks/useTableSort";
import { SortableHeader } from "../../components/SortableHeader";

const DEFAULT_LIMIT = 1000000; // change this if you want a different limit

const TrainingYouTube = () => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedYoutubeLink, setUploadedYoutubeLink] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState(null);

  // 🆕 summary states
  const [summaryDataList, setSummaryDataList] = useState([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  const [youtubeSummary, setYoutubeSummary] = useState({
    totalTokens: 0,
    totalCharacters: 0,
    limit: DEFAULT_LIMIT,
    usedPercent: 0,
    isOverLimit: false,
    overByChars: 0,
    breakdown: [],
  });

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  // ---------- Helpers to parse characters robustly ----------
  const getCharsFromItem = (item) => {
    // 1️⃣ If backend already sends characters (future-proof)
    if (item?.characters != null) return item.characters;

    // 2️⃣ Count characters from summary text (YouTube case)
    if (typeof item?.summary === "string") {
      return item.summary.length;
    }

    // 3️⃣ Fallback: tokens (optional, lower priority)
    if (item?.total_tokens != null) {
      // rough conversion if ever needed
      return item.total_tokens * 4;
    }

    return 0;
  };

  // ✅ New function: handle status change (approved / rejected / pending)
  const handleChangeStatus = async (fileId, newStatus) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/approve-or-reject`,
        { file_id: fileId, approval_status: newStatus, notes: "" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showSuccessToast(`Status updated to ${newStatus}`);
      fetchYoutubeLinks();
    } catch (err) {
      console.error("❌ Error updating status:", err);
      showErrorToast("Failed to update status");
    }
  };

  const parseChars = (val) => {
    if (val == null) return 0;
    if (typeof val === "number") return Number.isFinite(val) ? val : 0;

    // try to parse numeric-like strings (handles "1,234" etc.)
    const str = String(val).replace(/,/g, "").trim();
    const n = Number(str);
    if (Number.isFinite(n)) return n;

    // fallback: extract first numeric sequence
    const m = String(val).match(/-?\d+(\.\d+)?/);
    return m ? Number(m[0]) : 0;
  };
  const getTokensFromItem = (item) => {
    return item?.total_tokens ?? item?.tokens ?? item?.token_count ?? 0;
  };

  // ---------- Compute summary ----------
  const computeYoutubeSummary = (youtubeLinks = []) => {
    const limit = DEFAULT_LIMIT;

    if (!Array.isArray(youtubeLinks) || youtubeLinks.length === 0) {
      return {
        totalCharacters: 0,
        totalTokens: 0,
        limit,
        usedPercent: 0,
        isOverLimit: false,
        overByChars: 0,
        breakdown: [],
      };
    }

    const activeVideos = youtubeLinks.filter((v) => v.status === "approved");

    if (activeVideos.length === 0) {
      return {
        totalCharacters: 0,
        totalTokens: 0,
        limit,
        usedPercent: 0,
        isOverLimit: false,
        overByChars: 0,
        breakdown: [],
      };
    }

    // 🔢 TOTAL CHARACTERS
    const totalCharacters = activeVideos.reduce(
      (sum, item) => sum + parseChars(getCharsFromItem(item)),
      0,
    );

    // 🆕 🔢 TOTAL TOKENS
    const totalTokens = activeVideos.reduce(
      (sum, item) => sum + parseChars(getTokensFromItem(item)),
      0,
    );

    // 📊 PERCENT
    const rawPercent = limit > 0 ? (totalCharacters / limit) * 100 : 0;
    const usedPercent = Math.min(Number(rawPercent.toFixed(1)), 100);

    // 🚨 LIMIT CHECK
    const isOverLimit = totalCharacters > limit;
    const overByChars = isOverLimit ? totalCharacters - limit : 0;

    // 🧩 BREAKDOWN (unchanged)
    const typeMap = {};

    activeVideos.forEach((item) => {
      const url = String(item?.url || "").toLowerCase();
      let type = "Other";

      if (url.includes("youtube.com") || url.includes("youtu.be"))
        type = "YouTube";
      else if (url.endsWith(".mp4")) type = "MP4";
      else if (url.endsWith(".mov")) type = "MOV";
      else if (url.endsWith(".avi")) type = "AVI";
      else if (url.endsWith(".mkv")) type = "MKV";
      else if (url.endsWith(".webm")) type = "WebM";

      const chars = parseChars(getCharsFromItem(item));
      typeMap[type] = (typeMap[type] || 0) + chars;
    });

    const breakdown = Object.entries(typeMap)
      .map(([type, chars]) => ({
        type,
        chars,
        value: Math.round((chars / totalCharacters) * 100),
      }))
      .sort((a, b) => b.value - a.value);

    return {
      totalCharacters,
      totalTokens, // 🆕
      limit,
      usedPercent,
      isOverLimit,
      overByChars,
      breakdown,
    };
  };

  useEffect(() => {
    const computed = computeYoutubeSummary(uploadedYoutubeLink);

    setYoutubeSummary((prev) => {
      if (
        prev.totalCharacters === computed.totalCharacters &&
        prev.totalTokens === computed.totalTokens && // 🆕
        prev.usedPercent === computed.usedPercent &&
        prev.breakdown.length === computed.breakdown.length
      ) {
        return prev;
      }
      return computed;
    });
  }, [uploadedYoutubeLink]);

  // ---------- Fetch youtube links ----------
  useEffect(() => {
    fetchYoutubeLinks();
    // fetchSummaryFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchYoutubeLinks = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/youtube-urls`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // be resilient to response shape
      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.uploaded_youtube_urls || res.data?.data || [];

      setUploadedYoutubeLink(data);
      // console.log("jasfjsjfosdfxvcojviodh",data)

      // compute summary immediately after fetch
      const computed = computeYoutubeSummary(data);
      setYoutubeSummary(computed);
    } catch (err) {
      console.error("❌ Error fetching uploaded Video  URLs:", err);
      showErrorToast("Error loading Video URL(s) links");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Upload, regenerate, modal logic ----------

  // 🆕 Fetch summary after upload
  const fetchSummaryFromBackend = async (uploadIds) => {
    try {
      // console.log("uploadIds", uploadIds);
      const idsQuery = uploadIds?.join(",");
      // console.log("idsQuery", idsQuery);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/summary?ids=${idsQuery}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // console.log("Resposne", res.data);
      setSummaryDataList(res.data);
      setShowSummaryModal(true);
    } catch (err) {
      console.error("Failed to fetch summaries:", err);
      showErrorToast("Failed to fetch summaries.");
    }
  };

  // 🆕 Regenerate specific summary
  const regenerateSummary = async (fileId, index) => {
    try {
      setLoadingIndex(index); // 🚫 disable button for this index

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/regenerate-summary`,
        { file_id: fileId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const newSummary = res.data.summary;

      setSummaryDataList((prevList) => {
        const updatedList = [...prevList];
        updatedList[index] = {
          ...updatedList[index],
          summary: newSummary,
        };
        return updatedList;
      });

      showSuccessToast("Summary regenerated!");
    } catch (err) {
      console.error("Failed to regenerate summary:", err);
      showErrorToast("Regeneration failed.");
    } finally {
      setLoadingIndex(null); // ✅ re-enable button
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!youtubeUrl.trim()) {
      showErrorToast("Please enter a valid Video URL.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/newUpload_youtube`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({ youtube_urls: [youtubeUrl] }),
        },
      );

      const result = await response.json();
      // console.log("YouTube Upload Result:", result);

      // 🔥 backend duplicate response (HTTP 409)
      if (response.status === 409) {
        const msg =
          result?.message || "This video URL(s) already exists. No new upload.";
        showErrorToast(msg);
        setIsSubmitting(false);
        return;
      }

      // console.log("Releated Result ******", result);/

      const uploaded_count = result?.uploaded_urls?.length || 0;
      const duplicate_count = result?.duplicate_urls?.length || 0;
      const total = uploaded_count + duplicate_count;

      const successCount =
        result?.api_response?.summary?.videos_success?.length || 0;

      const failedCount =
        result?.api_response?.summary?.videos_failed?.length || 0;

      // 🔥 priority: true failure
      if (response.status === 409) {
        const msg =
          result?.message || "This video URL(s) already exists. No new upload.";
        showErrorToast(msg);
      }

      // 🔴 ALL FAILED
      else if (successCount === 0 && failedCount > 0) {
        showErrorToast("Video processing failed. Please try again.");
      }

      // 🟡 PARTIAL SUCCESS
      else if (successCount > 0 && failedCount > 0) {
        showSuccessToast(`${successCount} uploaded, ${failedCount} failed.`);
      }

      // 🟢 PURE SUCCESS
      else if (successCount > 0 && duplicate_count === 0) {
        showSuccessToast(`${successCount} video(s) uploaded successfully.`);
      }

      // 🔵 DUPLICATES ONLY
      else if (duplicate_count > 0 && successCount === 0) {
        showErrorToast("This video URL(s) already exists. No new upload.");
      }

      // reset input
      setYoutubeUrl("");
      try {
        await fetchYoutubeLinks();
      } catch (e) {
        console.warn("fetchYoutubeLinks failed:", e);
      }

      // summary fetch for newly processed videos
      // const uploadedItems = result.uploaded_files || [];
      // const uploadIds = uploadedItems.map((item) => item.python_file_uuid);

      // if (uploadIds.length > 0) {
      //   await fetchSummaryFromBackend(uploadIds);
      // }
    } catch (error) {
      console.error("Failed to process video URL(s):", error);
      showErrorToast("Failed to process video URL(s). Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    alert(`Delete not implemented. File ID: ${id}`);
    // Implement DELETE API if needed
  };

  // ✅ Utility to detect and render clickable links (without "YouTube:" prefix)
  const renderWithLinks = (text) => {
    if (!text) return null;

    // Remove "URL:" prefix if exists
    const cleanText = text.replace(/^YouTube:\s*/i, "");

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return cleanText.split(urlRegex).map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
          style={{ color: "#0d6efd", fontWeight: 500 }}
        >
          {part}
        </a>
      ) : (
        part
      ),
    );
  };

  const { sortedData, requestSort, sortConfig } =
    useTableSort(uploadedYoutubeLink);
  return (
    <main
      className="container-fluid mt-2 h-100"
      style={{ paddingBottom: "800px" }}
    >
      <div className="row">
        {/* LEFT PANEL */}
        <div className="col-lg-8 mb-4">
          <div
            className="card profile border mb-4 p-4"
            // style={{ borderColor: "#e6e7ea" }}
          >
            <h6 className="fw-semibold" style={{ fontSize: "16px" }}>
              Video URLs
            </h6>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Add URL</label>
                <div
                  className="p-2 rounded"
                  // style={{
                  //   backgroundColor: "#FAF9F9",
                  //   border: "1px solid #3e3e3e",
                  // }}
                >
                  <input
                    type="url"
                    className="form-control "
                    // style={{ backgroundColor: "transparent" }}
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://example.com/your-link"
                    required
                    disabled={isSubmitting} // ⛔ input disabled while submitting
                  />
                </div>
              </div>
              <button
                type="submit"
                className="custom-btn  w-100 fw-semibold"
                // style={{ backgroundColor: "#3e3e3e" }}
                disabled={isSubmitting} // ⛔ button disabled while submitting
              >
                {isSubmitting ? "Uploading..." : "Upload"}
              </button>
            </form>
          </div>

          {/* Table */}
          <div className="mt-4">
            <div
              className="table-responsive"
              style={{ maxHeight: "330px", overflowY: "auto" }}
            >
              <table className="table text-left trainingUrls-table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <SortableHeader
                      column="url"
                      label="Video URL's"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      column="total_tokens"
                      label="Total Tokens"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      column="updatedDate"
                      label="Uploaded On"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      column="status"
                      label="Status"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="4">Loading...</td>
                    </tr>
                  ) : sortedData.length > 0 ? (
                    sortedData.map((item, index) => (
                      <tr key={item.id || index}>
                        <td
                          className="text-truncate"
                          style={{ maxWidth: "300px" }}
                          title={item.url}
                        >
                          {/* ✅ clickable link render */}
                          {renderWithLinks(item.url)}
                        </td>
                        <td>{item.total_tokens || 0}</td>
                        <td>{formatDateToUserTimeZone(item.updatedDate)}</td>
                        <td className="text-left">
                          <select
                            className={`badge ${
                              item.status === "approved"
                                ? "bg-success"
                                : item.status === "pending"
                                  ? "bg-warning"
                                  : "bg-danger "
                            }`}
                            value={item.status}
                            onChange={(e) =>
                              handleChangeStatus(item.id, e.target.value)
                            }
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </td>
                        {/* <td>
                          <button className="btn btn-sm btn-outline-dark">
                            <i className="bi bi-eye" />
                          </button>
                        </td> */}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="refresh-prompts ak-text-md">
                        No videos have been trained yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Summary */}
        <div className="col-lg-4">
          <div className="card profile p-4 shadow-sm">
            <h5 className="fw-semibold  mb-4 ak-text-md">Summary</h5>
            <div className="d-flex justify-content-center align-items-center mb-4 position-relative">
              <svg
                width="140"
                height="140"
                viewBox="0 0 36 36"
                className="circular-chart"
              >
                <path
                  className="circle-bg"
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#eee"
                  strokeWidth="2"
                />
                <path
                  className="circle"
                  strokeDasharray={`${youtubeSummary.usedPercent}, 100`}
                  stroke={youtubeSummary.isOverLimit ? "#dc3545" : "#3e3e3e"}
                  d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  strokeWidth="2"
                />
              </svg>
              <div className="position-absolute text-center">
                <div className="ak-text-md">
                  {youtubeSummary.isOverLimit ? "Limit Exceeded" : "Used"}
                </div>
                <div className="fw-bold fs-2">
                  {youtubeSummary.usedPercent}%
                </div>
              </div>
            </div>
            <div className="row text-center mb-4">
              <div className="col">
                <div className=" ak-text-md fw-semibold">Total Tokens</div>
                <div className="fw-semibold">
                  {youtubeSummary.totalTokens.toLocaleString()}
                </div>
              </div>
              <div className="col">
                <div className=" ak-text-md fw-semibold">Limit</div>
                <div className="fw-semibold">
                  {youtubeSummary.limit.toLocaleString()}
                </div>
              </div>
            </div>

            {youtubeSummary.breakdown.length > 0 ? (
              youtubeSummary.breakdown.map((item, idx) => (
                <div key={idx} className="mb-3">
                  <div className="d-flex justify-content-between ak-text-md fw-semibold">
                    <span className="text-secondary">{item.type}</span>
                    <span>{item.value}%</span>
                  </div>
                  <div className="progress" style={{ height: "6px" }}>
                    <div
                      className="progress-bar bg-dark"
                      style={{ width: `${item.value}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className=" text-center refresh-prompts small">
                No approved videos yet
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default TrainingYouTube;
